// src/components/enterprise/RunningHotPanel.jsx
//
// The two cohorts under strain, read by group. Needs Support (both dimensions
// under strain) is the one to act on and comes first; Running Hot (output
// high, wellbeing under strain) is the one a single wellness score hides, and
// the thing only this product scores. Each group's share sits beside the
// organization-wide share, so the comparable is the organization itself and
// the other groups, never a person. Everything here is aggregate: identities
// are never exposed and groups below the privacy floor are suppressed
// server-side.
//
// The third figure, SCREENING POSITIVE, is the two cohorts together: by
// construction a wellbeing score under 60 means at least one validated
// screener at moderate or worse (GAD-7 or PHQ-9 at 10 or more, the other
// screeners' equivalent), so Needs Support + Running Hot is the share of
// scored respondents in the clinical range. It is read against a published
// population figure, shown for adult organizations only.

import React from 'react';
import { Flame, LifeBuoy, Activity, Lock, ArrowRight, Users } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ConfidenceTag, SourceTag, MIN_GROUP_SIZE } from './shared/measurement';
import { useEdition } from '../../data/editions/EditionContext';
import { useOrganization } from '../../context/OrganizationContext';

// Statistics Canada, Survey on COVID-19 and Mental Health, February to May 2021
// (The Daily, 2021-09-27): 25% of Canadian adults screened positive for
// symptoms of major depressive disorder (PHQ-9 at 10+), generalized anxiety
// disorder (GAD-7 at 10+) or PTSD (PCL-5 at 33+); 21% in fall 2020. A
// population figure from the pandemic years, the most recent national one on
// the same instruments and thresholds; labelled as a reference, never as the
// organization's own.
export const POPULATION_SCREEN_REFERENCE = {
    share: 0.25,
    label: 'Canadian adults, spring 2021',
    source: 'Statistics Canada, Survey on COVID-19 and Mental Health (depression, anxiety or PTSD; 21% in fall 2020)',
    url: 'https://www150.statcan.gc.ca/n1/daily-quotidien/210927/dq210927a-eng.htm'
};

const pct = (v) => (typeof v === 'number' ? Math.round(v * 100) : null);
const sumShare = (a, b) => (typeof a === 'number' || typeof b === 'number') ? (a || 0) + (b || 0) : null;

// A group's share against the organization's, in points, as "+13" / "−4" / "".
const delta = (groupShare, orgShare) => {
    const g = pct(groupShare), o = pct(orgShare);
    if (g === null || o === null) return null;
    const d = g - o;
    return d === 0 ? '±0 pts' : d > 0 ? `+${d} pts` : `−${Math.abs(d)} pts`;
};

const CohortCard = ({ tone, Icon, title, count, share, responded, line, pending, reference }) => {
    const bg = tone === 'rose' ? 'bg-rose-50 text-rose-600' : tone === 'blue' ? 'bg-blue-50 text-[var(--brand-deep)]' : 'bg-amber-50 text-amber-600';
    return (
        <div className="rounded-xl border border-slate-100 p-4">
            <div className="flex items-center gap-2 mb-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bg}`}><Icon size={16} strokeWidth={2} /></div>
                <h4 className="text-sm font-semibold text-[var(--brand-deep)]">{title}</h4>
            </div>
            {pending ? (
                <p className="text-xs text-slate-500 font-light leading-relaxed">Appears as members complete their next check-in.</p>
            ) : (
                <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-semibold text-[var(--brand-deep)] tracking-tight">{count ?? '—'}</span>
                    <span className="text-xs font-medium text-slate-500">{share !== null ? `${share}% of ${responded ?? '—'} respondents` : 'people'}</span>
                </div>
            )}
            {!pending && reference && share !== null && (
                <p className="text-xs font-semibold mt-1" title={reference.source}>
                    <span className={share > pct(reference.share) ? 'text-rose-600' : 'text-emerald-700'}>{share > pct(reference.share) ? `${share - pct(reference.share)} points above` : share === pct(reference.share) ? 'Level with' : `${pct(reference.share) - share} points below`}</span>
                    <span className="text-slate-500 font-medium"> {reference.label}: {pct(reference.share)}% <span className="text-slate-400 font-normal">· population reference</span></span>
                </p>
            )}
            <p className="text-xs text-slate-500 font-light leading-relaxed mt-2">{line}</p>
        </div>
    );
};

const RunningHotPanel = ({ live, metrics, onNavigate }) => {
    const edition = useEdition();
    const { orgData } = useOrganization();
    // A youth organization's screeners are the youth set; the adult population
    // figure does not apply there, so the comparable is shown for adult orgs only.
    const adultOrg = !orgData || orgData.youthSurface !== 'athlete';
    const isLive = live?.source === 'live';
    const cohort = metrics?.cohort;
    const observed = isLive && cohort?.source === 'observed' && (cohort.runningHotShare !== null || cohort.needsSupportShare !== null);
    const pending = isLive && !observed;

    const responded = observed ? cohort.responded : (cohort?.responded ?? null);
    const nsShare = observed ? cohort.needsSupportShare : null;
    const nsCount = observed && typeof nsShare === 'number' ? Math.round(nsShare * (responded || 0)) : null;
    const rhShare = observed ? cohort.runningHotShare : null;
    const rhCount = observed ? cohort.runningHotCount : (edition.summary.highSignalCohort ?? null);
    const rhPct = observed ? pct(rhShare) : (responded ? Math.round(((rhCount || 0) / responded) * 100) : null);
    // Screening positive: the two cohorts together (see the header comment).
    const spShare = observed ? sumShare(nsShare, rhShare) : null;
    const spCount = observed && spShare !== null ? Math.round(spShare * (responded || 0)) : null;

    const trend = observed
        ? (live.capacityTrend || []).map(p => ({ date: p.date, pct: p.runningHotPct, ns: typeof p.needsSupportPct === 'number' ? p.needsSupportPct : null }))
        : (edition.capacityTrend || []).map(p => ({ date: p.date, pct: p.signal, ns: null }));
    const hasNs = observed && trend.some(p => p.ns !== null);

    // Groups by Needs Support share, then Running Hot: where the strain sits.
    const rows = observed
        ? (live.groups || [])
            .filter(g => !g.suppressed && (typeof g.needsSupportShare === 'number' || typeof g.runningHotShare === 'number'))
            .sort((a, b) => ((b.needsSupportShare ?? -1) - (a.needsSupportShare ?? -1)) || ((b.runningHotShare ?? -1) - (a.runningHotShare ?? -1)))
            .slice(0, 5)
        : (edition.readiness.groups || []).filter(g => g.responses >= MIN_GROUP_SIZE).slice(0, 5).map(g => ({ name: g.name, needsSupportShare: null, runningHotShare: null, pressure: g.pressure }));
    const suppressedCount = isLive ? (live.suppressedGroups || 0) : (edition.readiness.groups || []).filter(g => g.responses < MIN_GROUP_SIZE).length;

    return (
        <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
            <div className="px-6 md:px-8 pt-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-[var(--brand-deep)]">Needs Support and Running Hot</h3>
                        <SourceTag modeled={!observed} />
                        <ConfidenceTag level="Leading" />
                    </div>
                    <p className="text-xs text-slate-500 font-light mt-1">
                        Two cohorts, read by group against the whole organization, and together the share screening positive on a validated instrument. Needs Support is the one to act on; Running Hot is the one a single wellness score hides.
                    </p>
                </div>
                {onNavigate && (
                    <button onClick={() => onNavigate('cc-readiness')} className="flex items-center gap-1 text-[11px] font-semibold text-[var(--brand-blue)] hover:gap-2 transition-all shrink-0">
                        See every group <ArrowRight size={13} />
                    </button>
                )}
            </div>

            <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* the two numbers */}
                <div className="lg:col-span-1 space-y-3">
                    <CohortCard
                        tone="rose" Icon={LifeBuoy} title="Needs Support" pending={pending}
                        count={observed ? nsCount : null} share={observed ? pct(nsShare) : null} responded={responded}
                        line={observed ? 'Both dimensions under strain. The group to reach first, through the supports already in place.' : 'Both dimensions under strain. Shown once live check-ins are in; the modeled example carries Running Hot only.'}
                    />
                    <CohortCard
                        tone="amber" Icon={Flame} title="Running Hot" pending={pending}
                        count={rhCount} share={rhPct} responded={responded}
                        line="Output high, wellbeing under strain. It looks fine from the outside and is the population most associated with the claims that arrive later."
                    />
                    <CohortCard
                        tone="blue" Icon={Activity} title="Screening positive" pending={pending}
                        count={spCount} share={observed ? pct(spShare) : null} responded={responded}
                        reference={adultOrg ? POPULATION_SCREEN_REFERENCE : null}
                        line={observed ? 'At least one validated screener at moderate or worse (anxiety or depression at 10 or more). The two cohorts together; a screening result, not a diagnosis.' : 'The two cohorts together: at least one validated screener at moderate or worse. Shown once live check-ins are in.'}
                    />
                    <p className="text-[11px] text-slate-400 font-light leading-relaxed">
                        Individual identities are never exposed; groups below {MIN_GROUP_SIZE} respondents are suppressed.
                    </p>
                </div>

                {/* the trend over the table: one stacked column beside the three numbers */}
                <div className="lg:col-span-2 space-y-6">
                    {/* the trend, first */}
                    <div>
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-3">{observed ? `${hasNs ? 'The two cohorts' : 'Running Hot'} · ${live.windowDays}-day trend · % of respondents` : 'Running Hot · trend · modeled example'}</p>
                        <div className="h-44 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={trend} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="rhFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="nsFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#e11d48" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="#e11d48" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                                    <Area type="monotone" dataKey="pct" name="Running Hot %" stroke="#f59e0b" strokeWidth={2} fill="url(#rhFill)" connectNulls />
                                    {hasNs && <Area type="monotone" dataKey="ns" name="Needs Support %" stroke="#e11d48" strokeWidth={2} fill="url(#nsFill)" connectNulls />}
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                        {observed && trend.some(p => p.pct === null) && (
                            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1"><Lock size={10} /> weeks under {MIN_GROUP_SIZE} respondents are not plotted</p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">Four weekly points, the latest state per respondent.{hasNs ? '' : ' The Needs Support line appears once the snapshot function carries it.'}</p>
                    </div>

                    {/* where they concentrate, against the organization */}
                    <div>
                        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Users size={12} /> By group, against the organization</p>
                        {rows.length === 0 && <p className="text-xs text-slate-400 font-light">No group clears the reporting floor yet.</p>}
                        {rows.length > 0 && (
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                                        <th className="text-left font-semibold pb-2">Group</th>
                                        <th className="text-right font-semibold pb-2 text-rose-600">Needs Support</th>
                                        <th className="text-right font-semibold pb-2 text-amber-600">Running Hot</th>
                                        <th className="text-right font-semibold pb-2 text-[var(--brand-deep)]">Screening positive</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((g) => {
                                        const ns = observed ? pct(g.needsSupportShare) : null;
                                        const rh = observed ? pct(g.runningHotShare) : null;
                                        const dNs = observed ? delta(g.needsSupportShare, nsShare) : null;
                                        const dRh = observed ? delta(g.runningHotShare, rhShare) : null;
                                        const gSp = observed ? sumShare(g.needsSupportShare, g.runningHotShare) : null;
                                        const sp = observed ? pct(gSp) : null;
                                        const dSp = observed ? delta(gSp, spShare) : null;
                                        return (
                                            <tr key={g.name} className="border-t border-slate-100">
                                                <td className="py-2 pr-2 font-semibold text-slate-700 truncate max-w-[14rem]">{g.name}</td>
                                                <td className="py-2 text-right tabular-nums">
                                                    <span className="font-bold text-slate-800">{ns !== null ? `${ns}%` : '—'}</span>
                                                    {dNs && <span className={`ml-1 text-[10px] font-semibold ${dNs.startsWith('+') ? 'text-rose-600' : 'text-slate-400'}`}>{dNs}</span>}
                                                </td>
                                                <td className="py-2 text-right tabular-nums">
                                                    <span className="font-bold text-slate-800">{rh !== null ? `${rh}%` : '—'}</span>
                                                    {dRh && <span className={`ml-1 text-[10px] font-semibold ${dRh.startsWith('+') ? 'text-amber-600' : 'text-slate-400'}`}>{dRh}</span>}
                                                </td>
                                                <td className="py-2 text-right tabular-nums">
                                                    <span className="font-bold text-slate-800">{sp !== null ? `${sp}%` : '—'}</span>
                                                    {dSp && <span className={`ml-1 text-[10px] font-semibold ${dSp.startsWith('+') ? 'text-[var(--brand-deep)]' : 'text-slate-400'}`}>{dSp}</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                {observed && (
                                    <tfoot>
                                        <tr className="border-t border-slate-200 text-slate-500">
                                            <td className="pt-2 font-medium">Whole organization</td>
                                            <td className="pt-2 text-right tabular-nums font-semibold">{pct(nsShare) !== null ? `${pct(nsShare)}%` : '—'}</td>
                                            <td className="pt-2 text-right tabular-nums font-semibold">{pct(rhShare) !== null ? `${pct(rhShare)}%` : '—'}</td>
                                            <td className="pt-2 text-right tabular-nums font-semibold">{pct(spShare) !== null ? `${pct(spShare)}%` : '—'}</td>
                                        </tr>
                                        {adultOrg && (
                                            <tr className="text-slate-400">
                                                <td className="pt-1 font-medium" title={POPULATION_SCREEN_REFERENCE.source}>{POPULATION_SCREEN_REFERENCE.label} <span className="font-normal">· population reference</span></td>
                                                <td className="pt-1 text-right">{'—'}</td>
                                                <td className="pt-1 text-right">{'—'}</td>
                                                <td className="pt-1 text-right tabular-nums font-semibold">{pct(POPULATION_SCREEN_REFERENCE.share)}%</td>
                                            </tr>
                                        )}
                                    </tfoot>
                                )}
                            </table>
                        )}
                        <p className="text-[10px] text-slate-400 mt-2 leading-relaxed">Points above or below the organization-wide share. Sorted by Needs Support. Screening positive is the two cohorts together: every member of either has a validated screener at moderate or worse.</p>
                        {suppressedCount > 0 && (
                            <p className="text-[10px] text-slate-400 mt-2 flex items-start gap-1 leading-relaxed">
                                <Lock size={10} className="mt-0.5 shrink-0" /> {suppressedCount} group{suppressedCount > 1 ? 's' : ''} below the floor {suppressedCount > 1 ? 'are' : 'is'} rolled into the organization-wide figure only.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RunningHotPanel;
